import { AxiosError, AxiosInstance } from "axios"

import { AxiosLightError } from "@/lib/axios-utils"
import { nowMillis } from "@/lib/date-utils"
import {
  recordVoiceTrace,
  redactHeaders,
  traceEnabled,
} from "@/lib/voice-trace/voice-trace"

const BODY_SUMMARY_KEY_LIMIT = 12
const BODY_SUMMARY_FIELD_LIMIT = 12
const LENGTH_FIELD_NAMES = new Set([
  "transcript",
  "full_transcript",
  "body",
  "results",
  "messages",
])

type HttpTraceBodySummary =
  | { kind: "absent" }
  | { kind: "non-json-string"; byteLength: number }
  | { kind: "string"; byteLength: number }
  | JsonBodySummary
  | { kind: "other" }

interface JsonBodySummary {
  kind: "json"
  byteLength: number
  shape:
    | { type: "array"; length: number }
    | {
        type: "object"
        keys: string[]
        keyCount: number
        fieldLengths: Record<string, number>
      }
    | { type: "scalar" }
}

function requestSummary(method: string | undefined, url: string | undefined) {
  return `${(method ?? "get").toUpperCase()} ${url ?? ""}`
}

function encodedLength(value: string) {
  return new TextEncoder().encode(value).length
}

function fieldLength(value: unknown) {
  if (typeof value === "string") return encodedLength(value)

  const serialized = JSON.stringify(value)
  return typeof serialized === "string" ? encodedLength(serialized) : 0
}

function jsonBodyShape(value: unknown): JsonBodySummary["shape"] {
  if (Array.isArray(value)) {
    return { type: "array", length: value.length }
  }

  if (typeof value !== "object" || value === null) {
    return { type: "scalar" }
  }

  const entries = Object.entries(value)
  const fieldLengths = Object.fromEntries(
    entries
      .filter(([key]) => LENGTH_FIELD_NAMES.has(key))
      .slice(0, BODY_SUMMARY_FIELD_LIMIT)
      .map(([key, fieldValue]) => [key, fieldLength(fieldValue)])
  )

  return {
    type: "object",
    keys: entries.map(([key]) => key).slice(0, BODY_SUMMARY_KEY_LIMIT),
    keyCount: entries.length,
    fieldLengths,
  }
}

function jsonBodySummary(value: unknown, byteLength: number): JsonBodySummary {
  return {
    kind: "json",
    byteLength,
    shape: jsonBodyShape(value),
  }
}

function httpBodySummary(data: unknown): HttpTraceBodySummary {
  if (data === undefined || data === null || data === "") {
    return { kind: "absent" }
  }

  if (typeof data !== "string") {
    const serialized = JSON.stringify(data)
    if (typeof serialized !== "string") return { kind: "other" }

    return jsonBodySummary(data, encodedLength(serialized))
  }

  const byteLength = encodedLength(data)
  try {
    return jsonBodySummary(JSON.parse(data), byteLength)
  } catch {
    return { kind: "non-json-string", byteLength }
  }
}

export function installVoiceHttpTrace(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    if (!traceEnabled()) return config

    config.voiceTraceStartedAt = nowMillis()
    recordVoiceTrace({
      category: "http",
      level: "info",
      summary: requestSummary(config.method, config.url),
      detail: {
        method: config.method,
        url: config.url,
        headers: redactHeaders(config.headers),
        body: httpBodySummary(config.data),
      },
    })

    return config
  })

  instance.interceptors.response.use(
    (response) => {
      if (!traceEnabled()) return response

      const startedAt = response.config.voiceTraceStartedAt
      recordVoiceTrace({
        category: "http",
        level: "info",
        summary: requestSummary(response.config.method, response.config.url),
        detail: {
          method: response.config.method,
          url: response.config.url,
          status: response.status,
          durationMs: startedAt !== undefined ? nowMillis() - startedAt : null,
          serverTiming: response.headers["server-timing"],
          headers: redactHeaders(response.headers),
          body: httpBodySummary(response.data),
        },
      })

      return response
    },
    (error) => {
      if (
        traceEnabled() &&
        (error instanceof AxiosError || error instanceof AxiosLightError)
      ) {
        const request =
          error instanceof AxiosError
            ? {
                method: error.config?.method,
                url: error.config?.url,
                status: error.response?.status,
                code: error.code,
                startedAt: error.config?.voiceTraceStartedAt,
                headers: error.response?.headers,
                body: error.response?.data,
              }
            : {
                method: error.request?.method?.toLowerCase(),
                url: error.request?.url,
                status: error.response?.status,
                code: undefined,
                startedAt: error.request?.startedAt,
                headers: error.response?.headers,
                body: error.response?.data,
              }
        recordVoiceTrace({
          category: "http",
          level: "error",
          summary: requestSummary(request.method, request.url),
          detail: {
            method: request.method,
            url: request.url,
            status: request.status ?? null,
            code: request.code,
            message: error.message,
            durationMs:
              request.startedAt !== undefined
                ? nowMillis() - request.startedAt
                : null,
            headers: redactHeaders(request.headers),
            body: httpBodySummary(request.body),
          },
        })
      }

      return Promise.reject(
        error instanceof Error ? error : new Error(`${error}`)
      )
    }
  )
}
